import {
  useCallback,
  useMemo,
  useRef,
  useState,
  useContext,
  useEffect
} from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GridOptions } from "ag-grid-enterprise";
import { Redirect, useHistory, useParams } from "react-router-dom";
import { Modal, Tooltip, message } from "antd";
import { ProjectContext, TProjectContext } from "context/ProjectProvider";
import CiqLogPageHeader from "components/ciq-log-page-header";
import { getGridStatusBudge } from "components/ciq-log-page-header/grid-status-budge";
import CiqAgSearch from "components/ciq-ag-search";
import {
  FilterChipComponent,
  FilterChipComponentRef
} from "entity-app/shared-components/log-render-components/filter-chip-component";
import { truncatedTextCellRenderer } from "components/cell-renders";
import { CiqIconBox } from "components/svg-icons";
import { GridLoadingIndicator } from "components/widgets";
import { useCIQQuery, useCIQQuerySubscription } from "hooks/ciq-gql-hooks";
import {
  QUERY_FETCH_SCHEDULE_OPC,
  QUERY_OPC_LAST_IMPORT_LOG
} from "services/graphQL/queries";
import { SUBSCRIPTION_INTG_CONFIGS } from "services/graphQL/ciq-queries";
import { comparatorWithEmptyAtBottom } from "utils/grid-utils";
import { ImportStatuses } from "utils/utils";
import dayjs from "utils/dayjs-config";
import {
  DATE_FORMAT_MMDDYYYY_HHMM_A,
  INPUT_DATE_FORMAT
} from "utils/dateutils";
import {
  exportOpcScheduleSourceInfo,
  OPC_EXPORT_FILE_TYPE
} from "services/integration-service";
import {
  getOpcExport,
  markOpcExportStarted,
  TOpcExportHandle
} from "services/opc-export-state";
import {
  getOpcSessionImportFailures,
  setOpcSessionImportFailures
} from "services/opc-import-failure-state";
import { useGetProjectINTGState } from "hooks/submittal";
import {
  simplifyProjectIntegrationConfigs,
  simplifySubscriptionIntegrationConfigs
} from "pages/subscription-settings/integrations-tab-new/utils";
import {
  getSchedulesInOpcColumnDefs,
  getImportScheduleDate,
  getImportScheduleName,
  getLatestImportStatus,
  getLastImportLogId,
  isScheduleImportActive,
  isScheduleImportProcessing,
  shouldShowOpcImportFailure,
  sortByBaselineDateDesc,
  TScheduleSourceInfo,
  TScheduleSourceInfoRow,
  withSerialNumbers
} from "./schedules-in-opc-grid-config";
import SchedulesInOpcSyncButton, {
  OPC_INTEGRATION_SYSTEM
} from "./schedules-in-opc-sync-button";
import useTrackedOpcSync from "./use-opc-sync";
import useTrackedOpcExports from "./use-opc-exports";
import OpcImportWatcher from "./opc-import-watcher";
import OpcSyncWatcher from "./opc-sync-watcher";
import OpcImportSuccessModal from "./opc-import-success-modal";
import SchedulesInOpcEmptyMessage from "./schedules-in-opc-empty-message";

const PAGE_TITLE = "Schedules in OPC";

// Shown until an OPC import has been recorded — a project that has never synced
// has nothing to report, and neither does a row whose jsonb metadata arrived
// without a timestamp.
const LAST_SYNCED_UNKNOWN = "Last synced: --";
const SYNC_IN_PROGRESS = "Sync is in progress";

const IMPORT_CONFIRM_TITLE = "Import Schedule?";
// The break after the first sentence separates what happens now from what the
// user can do afterwards. It renders as a break only because the content below
// carries `whitespace-pre-line` — the modal would otherwise collapse it back
// into a space and run the two sentences together.
const IMPORT_CONFIRM_MESSAGE =
  "This process will run in the background and may take a few minutes.\nOnce imported, you can view the Schedule in the list of Project Schedules.";

// Reported by the page rather than by ErrorManager, which only ever toasts its
// generic "Something has failed. Try again later." — the same reason the sync
// button carries its own wording.
const IMPORT_FAILURE_MESSAGE = "Schedule import failed. Please try again.";

type TOpcImportLogRow = {
  id: string;
  metadata: { processed_at?: string } | null;
};

function dropResolvedImportFailures(
  failedIds: ReadonlySet<string>,
  rows: TScheduleSourceInfoRow[]
): ReadonlySet<string> {
  if (failedIds.size === 0) return failedIds;
  let changed = false;
  const next = new Set(failedIds);
  failedIds.forEach((id) => {
    const row = rows.find((candidate) => candidate.id === id);
    const lastStatus = getLatestImportStatus(row);
    if (lastStatus && lastStatus !== (ImportStatuses.FAILED as string)) {
      next.delete(id);
      changed = true;
    }
  });
  return changed ? next : failedIds;
}

function SchedulesInOpc() {
  const gridRef = useRef<AgGridReact<TScheduleSourceInfoRow>>(null);
  const filterChipRef = useRef<FilterChipComponentRef>(null);

  const [searchText, setSearchText] = useState("");

  const { projectId } = useParams<{ projectId: string }>();
  const history = useHistory();

  const {
    gqlClientForProject,
    isFeatureFlagEnabled,
    isProductEntitlementsDataAvailable,
    tokenRetrievalState,
    isProjectArchived
  }: TProjectContext = useContext(ProjectContext);

  const isOpcIntegrationEnabled = isFeatureFlagEnabled("OPC_INTEGRATION_V1");

  const { data, loading, refetch } = useCIQQuery<{
    schedule_source_info: Array<TScheduleSourceInfo>;
  }>(QUERY_FETCH_SCHEDULE_OPC, {
    client: gqlClientForProject,
    skip: !gqlClientForProject || !isOpcIntegrationEnabled
  });

  // The newest OPC import, whoever started it — so the line is right on arrival,
  // not only after a sync this user triggered.
  const { data: lastImportData, refetch: refetchLastImport } = useCIQQuery<{
    import_log: Array<TOpcImportLogRow>;
  }>(QUERY_OPC_LAST_IMPORT_LOG, {
    client: gqlClientForProject,
    skip: !gqlClientForProject || !isOpcIntegrationEnabled
  });

  // OPC workspace + project names for the sync-source line. Read from project
  // integration configs (not ProjectContext.projectIntegrationConfig), which is
  // only loaded for submittal-integration projects — OPC need not be one.
  const { data: projectIntegrationData } = useGetProjectINTGState({
    client: gqlClientForProject,
    skip: !gqlClientForProject || !isOpcIntegrationEnabled
  });

  const { data: subscriptionIntgData } = useCIQQuerySubscription(
    SUBSCRIPTION_INTG_CONFIGS,
    {
      client: gqlClientForProject,
      skip: !gqlClientForProject || !isOpcIntegrationEnabled
    }
  );

  const opcIntegrationStatus = useMemo(() => {
    const projectConfigs =
      simplifyProjectIntegrationConfigs(
        projectIntegrationData?.project_integration_configs
      ) ?? [];
    const subscriptionConfigs =
      simplifySubscriptionIntegrationConfigs(
        subscriptionIntgData?.subscription_integration_configs
      ) ?? [];

    const hasProjectOpc = projectConfigs.some(
      (config: { integration?: { system?: string } }) =>
        config?.integration?.system === OPC_INTEGRATION_SYSTEM
    );
    const hasSubscriptionOpc = subscriptionConfigs.some(
      (config: { integration?: { system?: string } }) =>
        config?.integration?.system === OPC_INTEGRATION_SYSTEM
    );

    return { hasProjectOpc, hasSubscriptionOpc };
  }, [projectIntegrationData, subscriptionIntgData]);

  const opcSourceLabels = useMemo(() => {
    const configs =
      simplifyProjectIntegrationConfigs(
        projectIntegrationData?.project_integration_configs
      ) ?? [];
    const opcConfig = configs.find(
      (config: { integration?: { system?: string } }) =>
        config?.integration?.system === OPC_INTEGRATION_SYSTEM
    );
    return {
      workspace:
        opcConfig?.subscriptionProperties?.OPC_WORKSPACE_NAME ||
        opcConfig?.properties?.OPC_WORKSPACE_NAME ||
        "",
      project: opcConfig?.properties?.OPC_PROJECT_NAME || ""
    };
  }, [projectIntegrationData]);

  // Sort first, number second: Sr No. is stamped onto the row, so it has to be
  // assigned in the order the grid will actually show. Newest baseline first.
  const rowData = useMemo(
    () =>
      withSerialNumbers(
        sortByBaselineDateDesc(data?.schedule_source_info ?? [])
      ),
    [data]
  );

  const trackedSync = useTrackedOpcSync();
  const isSyncing = trackedSync?.projectId === projectId;

  // The query only ever returns PROCESSED rows, so the timestamp it carries is
  // by definition the last successful sync — a running or failed run cannot move
  // it. In-progress therefore comes from the tracked handle, which knows about
  // the run from the moment it is kicked off, before any row exists to read.
  //
  // .tz() shifts to the project timezone ProjectProvider set as the dayjs
  // default. Without it the user reads machine-local time, contradicting every
  // other clock on the project.
  const syncStatus = useMemo(() => {
    if (isSyncing) return SYNC_IN_PROGRESS;
    const processedAt = lastImportData?.import_log?.[0]?.metadata?.processed_at;
    if (!processedAt) return LAST_SYNCED_UNKNOWN;
    return `Last synced: ${dayjs(processedAt, INPUT_DATE_FORMAT)
      .tz()
      .format(DATE_FORMAT_MMDDYYYY_HHMM_A)}`;
  }, [lastImportData, isSyncing]);

  // Same "(From: Workspace - Project) | Last synced: …" shape as the submittal
  // log's IntegrationProjectInfo. Degrades segment-by-segment when a name has
  // not resolved yet, so no dangling separator is left behind.
  const integrationSourceLabel = useMemo(() => {
    const { workspace, project } = opcSourceLabels;
    return [workspace, project].filter(Boolean).join(" - ");
  }, [opcSourceLabels]);

  const integrationLabelRef = useRef<HTMLSpanElement>(null);
  const [showIntegrationInfoTooltip, setShowIntegrationInfoTooltip] =
    useState(false);

  const integrationInfoTooltip = useMemo(
    () =>
      integrationSourceLabel
        ? `(From: ${integrationSourceLabel}) | ${syncStatus}`
        : syncStatus,
    [integrationSourceLabel, syncStatus]
  );

  // Re-read once polling is done. OpcSyncWatcher clears the tracked
  // handle the moment its poll reaches a terminal status, so this fires exactly
  // then — and it has to cover the grid as well as the timestamp, since the sync
  // is precisely what changed the rows the user may be staring at.
  const wasSyncingRef = useRef(false);
  useEffect(() => {
    if (wasSyncingRef.current && !isSyncing) {
      refetch();
      refetchLastImport();
    }
    wasSyncingRef.current = isSyncing;
  }, [isSyncing, refetch, refetchLastImport]);

  const trackedImports = useTrackedOpcExports();

  // Schedules whose import failed this session — covers the window before the
  // refetch lands with FAILED on the row, and feeds the sub-tab failure dot.
  const [failedImportScheduleIds, setFailedImportScheduleIds] = useState<
    ReadonlySet<string>
  >(() => getOpcSessionImportFailures(projectId));

  // Ids that finished PROCESSED this session. Outranks a stale FAILED session
  // entry (and briefly-stale row data) so success does not keep looking failed.
  const [recentlyProcessedIds, setRecentlyProcessedIds] = useState<
    ReadonlySet<string>
  >(() => new Set());

  // ag-grid keeps the getRowClass fn from the last gridOptions it applied and
  // does not reliably swap it when the prop identity changes. The Actions cell
  // reads the export store live (hence the spinner updates on retry) while a
  // closed-over trackedImports left the pink tint stuck. Read failure overrides
  // through this ref, and the in-flight check through getOpcExport, so redraws
  // always see current state.
  const rowClassStateRef = useRef({
    failedImportScheduleIds,
    recentlyProcessedIds
  });
  rowClassStateRef.current = {
    failedImportScheduleIds,
    recentlyProcessedIds
  };

  // Reload this project's session failures when the route project changes —
  // otherwise the sync effect below would write the previous project's set
  // under the new key before React applied this state update.
  useEffect(() => {
    setFailedImportScheduleIds(getOpcSessionImportFailures(projectId));
    setRecentlyProcessedIds(new Set());
  }, [projectId]);

  // Keep the sub-tab failure dot in sync for the pre-refetch window (and while
  // the user is on Schedules). Deliberately keyed only on the set: including
  // projectId would re-run on switch with the stale set still in state.
  useEffect(() => {
    setOpcSessionImportFailures(projectId, failedImportScheduleIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- projectId from latest render only; see comment above
  }, [failedImportScheduleIds]);

  // Drop session failures as soon as row data shows a non-FAILED
  // latest_import_status — same moment the spinner yields once PROCESSING ends.
  useEffect(() => {
    setFailedImportScheduleIds((prev) =>
      dropResolvedImportFailures(prev, rowData)
    );
  }, [rowData]);

  // Confirmation and POST live here rather than in the cell renderer: the page
  // owns the token and the refetch, and a renderer is destroyed and recreated on
  // every row-data refresh and on scroll virtualization, which would orphan the
  // async continuation mid-flight.
  const confirmingRef = useRef<Set<string>>(new Set());

  const onImportSchedule = useCallback(
    (row?: TScheduleSourceInfo) => {
      // Guards a second modal for the same row while the first is open. Once it
      // closes the cell is a spinner, so there is no button left to click.
      if (isProjectArchived || !row?.id || confirmingRef.current.has(row.id))
        return;
      const scheduleId = row.id;
      confirmingRef.current.add(scheduleId);

      Modal.confirm({
        title: IMPORT_CONFIRM_TITLE,
        content: (
          <div className="whitespace-pre-line">{IMPORT_CONFIRM_MESSAGE}</div>
        ),
        okText: "Confirm",
        cancelText: "Cancel",
        afterClose: () => confirmingRef.current.delete(scheduleId),
        onOk: async () => {
          const response = await exportOpcScheduleSourceInfo(
            scheduleId,
            {
              schedule_name: getImportScheduleName(row),
              schedule_date: getImportScheduleDate(row),
              file_type: OPC_EXPORT_FILE_TYPE
            },
            tokenRetrievalState?.token,
            // Reported below instead, so the user reads what actually failed
            // rather than ErrorManager's generic toast.
            false
          );
          // The id is what the watcher polls. Without one there is nothing to
          // track, and a spinner nothing can clear is worse than none — so a
          // response that carries no id is reported as a failure too, since
          // nothing downstream will ever report it.
          const importLogId = response?.data?.import_log_id;
          if (!response?.success || !importLogId) {
            setFailedImportScheduleIds((prev) => new Set(prev).add(scheduleId));
            message.error(IMPORT_FAILURE_MESSAGE);
            return;
          }

          // A retry is underway — clear any prior failure tint before the
          // spinner takes over.
          setFailedImportScheduleIds((prev) => {
            if (!prev.has(scheduleId)) return prev;
            const next = new Set(prev);
            next.delete(scheduleId);
            return next;
          });
          setRecentlyProcessedIds((prev) => {
            if (!prev.has(scheduleId)) return prev;
            const next = new Set(prev);
            next.delete(scheduleId);
            return next;
          });

          markOpcExportStarted({
            scheduleId,
            projectId,
            importLogId,
            startedAt: Date.now()
          });
          message.info("Import Started");
          refetch();
        }
      });
    },
    [projectId, refetch, tokenRetrievalState, isProjectArchived]
  );

  // The import that just landed, and the modal's own open state — a handle means
  // "finished, not yet acknowledged". Kept as one value rather than a boolean
  // plus a handle, so the dialog can never be open without the import it is
  // offering to review.
  const [finishedImport, setFinishedImport] = useState<TOpcExportHandle | null>(
    null
  );

  const onImportFinished = useCallback(
    (handle: TOpcExportHandle, status: ImportStatuses) => {
      if (status === ImportStatuses.PROCESSED) {
        // A dialog rather than a toast: the schedule is imported but not active,
        // and reviewing its impact first is a step worth stopping for.
        setFinishedImport(handle);
        setFailedImportScheduleIds((prev) => {
          if (!prev.has(handle.scheduleId)) return prev;
          const next = new Set(prev);
          next.delete(handle.scheduleId);
          return next;
        });
        setRecentlyProcessedIds((prev) => new Set(prev).add(handle.scheduleId));
      } else {
        // Inline failed-row UI replaces the toast for poll-finished failures.
        setRecentlyProcessedIds((prev) => {
          if (!prev.has(handle.scheduleId)) return prev;
          const next = new Set(prev);
          next.delete(handle.scheduleId);
          return next;
        });
        setFailedImportScheduleIds((prev) =>
          new Set(prev).add(handle.scheduleId)
        );
      }
      // The import is precisely what changed these rows — and the refetch is
      // also what puts the new version's import_log id on the row, which is what
      // the review link below is built from.
      refetch();
      refetchLastImport();
    },
    [refetch, refetchLastImport]
  );

  const onSkipChangeImpact = useCallback(() => setFinishedImport(null), []);

  // selectedVersionId is what Change Impact reads out of the query string to
  // preselect its version dropdown, keyed on import_log.id.
  //
  // Resolved from the row rather than from the handle: the handle carries the id
  // the export POST returned, which names the OPC job, and the version dropdown
  // only ever lists logs that a schedule_import_details row points at. Where the
  // two differ, the id would match no option — antd renders the raw uuid in
  // place of the name — and the impact endpoint would resolve it to a log with
  // no s3 prefix, which is what surfaces as "Missing required key 'Key' in
  // params". The handle stays as the fallback for the case where they are the
  // same id and the refetch above has not landed yet.
  const onViewChangeImpact = useCallback(() => {
    const importedRow = rowData.find(
      (row) => row.id === finishedImport?.scheduleId
    );
    const versionId =
      getLastImportLogId(importedRow) ?? finishedImport?.importLogId;

    history.push({
      pathname: `/project/${projectId}/schedule/change-impact`,
      search: `?selectedVersionId=${versionId}`
    });
    setFinishedImport(null);
  }, [history, projectId, finishedImport, rowData]);

  // Whether ANY row is mid-import, which is what greys out every other row's
  // action. Derived here rather than in the renderer because a cell only ever
  // sees its own row. Covers imports this user did not start; the ones they did
  // are already in the tracked store, which the renderers read directly.
  const isImportRunning = useMemo(
    () => rowData.some(isScheduleImportProcessing),
    [rowData]
  );

  // Rebuilt whenever isImportRunning flips, which only happens alongside a
  // row-data change — and ag-grid writes a new context into gridOptions before
  // it dispatches the row-data change that refreshes the cells, so the cells
  // never read the old flag against the new rows.
  const gridContext = useMemo(
    () => ({
      onImportSchedule,
      projectId,
      isImportRunning,
      isProjectArchived,
      failedImportScheduleIds,
      recentlyProcessedIds
    }),
    [
      onImportSchedule,
      projectId,
      isImportRunning,
      isProjectArchived,
      failedImportScheduleIds,
      recentlyProcessedIds
    ]
  );

  const columnDefs = useMemo<ColDef[]>(() => getSchedulesInOpcColumnDefs(), []);

  // Mirrors the submittal log's gridDefaultColDef: filtering is on by default and
  // columns opt out, rather than each column opting in. `filter: true` resolves to
  // the set filter (checkbox list of the distinct values) because App.tsx registers
  // AllEnterpriseModule — the same filter the submittal log's string columns get.
  //
  // Cell tooltips come from the renderer rather than ag-grid, again as the submittal
  // log does it: truncatedTextCellRenderer wraps the value in TruncatedTextWithTooltip,
  // an antd tooltip that opens only once the text is actually clipped. It is the
  // read-only twin of the submittal log's CellValueRendererWithEditIcon — same tooltip,
  // without the edit affordance this grid must not offer.
  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      resizable: true,
      autoHeight: true,
      filter: true,
      minWidth: 100,
      cellRenderer: truncatedTextCellRenderer,
      comparator: comparatorWithEmptyAtBottom
    }),
    []
  );

  const gridOptions = useMemo<GridOptions>(
    () => ({
      defaultColDef,
      suppressMenuHide: true,
      getRowClass: ({ data: row }) => {
        // Live store read — same source the Actions spinner uses — so a retry
        // clears the pink tint the moment the import is tracked, even if
        // ag-grid is still holding this callback from an earlier render.
        const isImporting =
          Boolean(row?.id && getOpcExport(row.id)) ||
          isScheduleImportProcessing(row);
        const {
          failedImportScheduleIds: failedIds,
          recentlyProcessedIds: processedIds
        } = rowClassStateRef.current;

        if (
          shouldShowOpcImportFailure(row, failedIds, {
            isImporting,
            recentlyProcessedIds: processedIds
          })
        ) {
          return "opc-schedule-import-failed-row";
        }
        return isScheduleImportActive(row) ? "schedule-active-row" : undefined;
      }
    }),
    [defaultColDef]
  );

  // Force a row redraw when failure / success sets or tracked imports change so
  // the tint updates without waiting on a row-data refetch.
  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    // Re-apply getRowClass explicitly: replacing gridOptions alone does not
    // always refresh the callback ag-grid invoked on redraw.
    api.setGridOption?.("getRowClass", gridOptions.getRowClass);
    api.redrawRows?.();
    api.refreshCells?.({ force: true });
  }, [
    failedImportScheduleIds,
    recentlyProcessedIds,
    trackedImports,
    gridOptions
  ]);

  // Rebuilt from the grid's own filter model on every filter change, so the chips
  // and the grid can never disagree.
  const onFiltersApplied = useCallback(() => {
    filterChipRef.current?.onFilterApplied();
  }, []);

  // The search box is controlled so the chips' "Clear All" — which clears the
  // grid's quickFilterText — can empty the input too. Left uncontrolled, the box
  // would keep showing a search term the grid is no longer applying.
  const onSearch = useCallback((text: string) => {
    setSearchText(text);
    gridRef.current?.api?.setGridOption("quickFilterText", text);
  }, []);

  const onSearchTextClear = useCallback(() => setSearchText(""), []);

  // The count pill is composed here rather than passed as CiqLogPageHeader's
  // titleParam: this header is two lines, and the header's own badge slot centres
  // against the whole block, which would drop the pill below the title.
  const countBudge = useMemo(
    () =>
      getGridStatusBudge({
        title: PAGE_TITLE,
        totalCount: rowData.length,
        filterStats: undefined
      }),
    [rowData.length]
  );

  const entityName = (
    <div className="flex items-start space-x-2">
      <CiqIconBox feature="SCHEDULES_OPC" size={20} padding={8} />
      <div className="flex flex-col gap-y-0.5 min-w-0 pl-1">
        <div className="flex items-center gap-x-2">
          <div className="text-black text-xl font-semibold leading-tight">
            {PAGE_TITLE}
          </div>
          {countBudge}
        </div>
        {/* Integration source line: where the schedules came from, and when they
            were last pulled. */}
        <div className="flex items-center space-x-1 min-w-0 pr-2">
          <Tooltip
            title={integrationInfoTooltip}
            placement="bottom"
            open={showIntegrationInfoTooltip}
            onOpenChange={(open) => {
              const el = integrationLabelRef.current;
              const overflowing = !!el && el.scrollWidth > el.clientWidth;
              setShowIntegrationInfoTooltip(open && overflowing);
            }}
          >
            <div
              className="flex items-center min-w-0 flex-1 text-black text-opacity-80 text-xs font-normal"
              data-testid="schedules-in-opc-integration-info"
            >
              {integrationSourceLabel && (
                <>
                  <span className="mr-1 shrink-0">(From:</span>
                  <span
                    ref={integrationLabelRef}
                    className="min-w-0 flex-1 truncate"
                  >
                    {integrationSourceLabel}
                  </span>
                  <span className="shrink-0">)</span>
                  <span className="ml-1 shrink-0">| {syncStatus}</span>
                </>
              )}
              {!integrationSourceLabel && (
                <span className="shrink-0">{syncStatus}</span>
              )}
            </div>
          </Tooltip>
          <SchedulesInOpcSyncButton />
        </div>
      </div>
    </div>
  );

  // Filter summary first, search second: CiqLogPageHeader lays `items` out in a
  // left-to-right flex row, and the submittal log puts its FilterChips
  // immediately before the search input.
  const headerItems = [
    <FilterChipComponent
      key="SchedulesInOpc_filter_chips"
      gridRef={gridRef}
      ref={filterChipRef}
      onSearchTextClear={onSearchTextClear}
    />,
    <CiqAgSearch
      key="SchedulesInOpc_search"
      gridRef={gridRef}
      placeholder="Search Schedules"
      value={searchText}
      onSearch={onSearch}
      data-testid="schedules-in-opc-search"
    />
  ];

  // Entitlements resolve asynchronously and the flag reads false until they land,
  // so hold off rather than bouncing a user who does have access.
  if (!isProductEntitlementsDataAvailable) return null;

  // The tab is hidden when the flag is off; this closes the direct URL too.
  if (!isOpcIntegrationEnabled) {
    return <Redirect to={`/project/${projectId}/schedule/project-schedules`} />;
  }

  const showOpcConfigEmptyState =
    !opcIntegrationStatus.hasProjectOpc &&
    Boolean(projectIntegrationData) &&
    Boolean(subscriptionIntgData);

  return (
    <div className="schedules-in-opc flex flex-col h-full">
      {/* Watches the project-level sync started from the header's button and
          toasts its outcome. Renders null; polls only while a sync is tracked. */}
      <OpcSyncWatcher />
      {/* One poller per running import, so nothing polls while the grid is
          idle. Each renders null and clears its own handle on a terminal
          status. */}
      {Object.values(trackedImports)
        .filter((handle) => handle.projectId === projectId)
        .map((handle) => (
          <OpcImportWatcher
            key={handle.scheduleId}
            handle={handle}
            onFinished={onImportFinished}
          />
        ))}
      {/* Replaces the success toast a finished import used to get: the schedule
          is in CIQ but not active, so this offers the review step instead of
          self-dismissing. */}
      <OpcImportSuccessModal
        open={Boolean(finishedImport)}
        onSkip={onSkipChangeImpact}
        onViewChangeImpact={onViewChangeImpact}
      />
      <CiqLogPageHeader
        entityName={entityName}
        items={headerItems}
        data-testid="schedules-in-opc-header"
      />
      <div
        className="relative flex flex-col h-full ag-theme-alpine"
        data-testid="schedules-in-opc-grid"
      >
        {/* Custom empty state when OPC is not configured on the project.
            Pass [] (not undefined) so ag-grid does not show its loading
            overlay on top of the "Click here to configure…" message. */}
        {showOpcConfigEmptyState && (
          <SchedulesInOpcEmptyMessage
            hasSubscriptionOpc={opcIntegrationStatus.hasSubscriptionOpc}
          />
        )}
        <AgGridReact<TScheduleSourceInfoRow>
          ref={gridRef}
          rowData={
            showOpcConfigEmptyState
              ? []
              : loading || !opcIntegrationStatus.hasProjectOpc
                ? undefined
                : rowData
          }
          columnDefs={columnDefs}
          gridOptions={gridOptions}
          defaultColDef={defaultColDef}
          context={gridContext}
          onGridReady={() => gridRef.current?.api?.sizeColumnsToFit()}
          onFilterChanged={onFiltersApplied}
          getRowId={({ data: row }) => row.id}
          overlayNoRowsTemplate={
            opcIntegrationStatus.hasProjectOpc
              ? "No schedules to show."
              : "<span></span>"
          }
          loadingOverlayComponent={
            showOpcConfigEmptyState ? undefined : GridLoadingIndicator
          }
          animateRows={false}
          suppressContextMenu
          enableCellTextSelection
        />
      </div>
    </div>
  );
}

export default SchedulesInOpc;
